<template>
  <form @submit.prevent>
    <section class="well-section">
      <div class="b-card-title">Brunnsdata</div>
      <div class="brunnsdata-layout">
        <div class="brunnsdata-fields">
          <div class="form-group">
            <label :for="`littera-${uid}`">Brunnens littera *</label>
            <input :id="`littera-${uid}`" v-model="form.littera" type="text" maxlength="50" />
          </div>
          <div class="form-group">
            <label :for="`outflowHeight-${uid}`">Höjd vattengång utgående mm</label>
            <input
              :id="`outflowHeight-${uid}`"
              v-digits
              v-model="form.outflowHeight"
              type="text"
              inputmode="numeric"
              maxlength="6"
            />
          </div>
          <div class="form-group">
            <label :for="`finishedHeight-${uid}`">Höjd färdig mark mm</label>
            <input
              :id="`finishedHeight-${uid}`"
              v-digits
              v-model="form.finishedHeight"
              type="text"
              inputmode="numeric"
              maxlength="6"
            />
          </div>
          <div class="form-group">
            <label :for="`dimension-${uid}`">Brunnsdimension Ø mm</label>
            <input
              :id="`dimension-${uid}`"
              v-digits
              v-model="form.dimension"
              type="text"
              inputmode="numeric"
              maxlength="6"
            />
          </div>
        </div>
        <div class="dial-image-wrapper">
          <img
            class="dial-image"
            :src="require('@/assets/dial-background.png')"
            alt="Dial Background"
          />
          <svg
            v-if="inletArrows.length"
            class="dial-overlay"
            viewBox="0 0 281 283"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g v-for="a in inletArrows" :key="a.idx">
              <line class="inlet-line" :x1="a.x1" :y1="a.y1" :x2="a.x2" :y2="a.y2" />
              <polygon class="inlet-arrowhead" :points="a.head" />
              <circle class="inlet-badge-bg" :cx="a.lx" :cy="a.ly" r="11" />
              <text
                class="inlet-badge-text"
                :x="a.lx"
                :y="a.ly"
                text-anchor="middle"
                dominant-baseline="central"
              >
                {{ a.idx }}
              </text>
            </g>
          </svg>
        </div>
      </div>

      <div class="form-group">
        <label>Vallning</label>
        <div class="radio-button-group-body">
          <input :id="`vallning-ja-${uid}`" type="radio" value="Ja" v-model="form.vallning" />
          <label :for="`vallning-ja-${uid}`">Ja</label>
          <input :id="`vallning-nej-${uid}`" type="radio" value="Nej" v-model="form.vallning" />
          <label :for="`vallning-nej-${uid}`">Nej</label>
        </div>
      </div>
      <div class="form-group">
        <label>Betäckning</label>
        <div class="radio-button-group-body">
          <input :id="`betackning-ja-${uid}`" type="radio" value="Ja" v-model="form.betackning" />
          <label :for="`betackning-ja-${uid}`">Ja</label>
          <input :id="`betackning-nej-${uid}`" type="radio" value="Nej" v-model="form.betackning" />
          <label :for="`betackning-nej-${uid}`">Nej</label>
        </div>
      </div>
    </section>

    <section class="well-section">
      <div class="b-card-title">Anslutningar</div>

      <div class="order-grid">
        <div class="order-head">
          <span>Anslutning</span>
          <span>Dimension mm</span>
          <span>Rörtyp/material</span>
          <span>Vinkel °</span>
          <span>Sandfång / Stalp mm</span>
          <span></span>
        </div>

        <div class="order-row" v-for="(row, idx) in form.items" :key="idx">
          <!-- Anslutning (readonly) -->
          <input v-model="row.anslutning" readonly type="text" />

          <!-- Dimension -->
          <input
            v-digits
            v-model="row.dimension"
            placeholder="Dimension mm"
            type="text"
            inputmode="numeric"
            maxlength="6"
          />

          <!-- Rörtyp / material som dropdown -->
          <select v-model="row.rortyp">
            <option disabled value="">Välj material / rörtyp</option>
            <option v-for="opt in rortypOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>

          <!-- Fast utloppsvinkel = 0 -->
          <input v-if="idx === 0" type="number" value="0" readonly />

          <!-- Vinkel för övriga – klampas till 0–360 i validateAngles() -->
          <input
            v-else
            v-digits
            v-model="row.vinkel"
            required
            @change="validateAngles"
            placeholder="Vinkel °"
            type="text"
            inputmode="numeric"
            maxlength="3"
          />

          <!-- Sandfång på första raden, Stalp på övriga -->
          <input
            v-if="idx === 0"
            v-digits
            v-model="row.sandfang"
            placeholder="Sandfång mm"
            type="text"
            inputmode="numeric"
            maxlength="6"
          />
          <input
            v-else
            v-digits
            v-model="row.stalp"
            placeholder="Stalp mm"
            type="text"
            inputmode="numeric"
            maxlength="6"
          />

          <!-- Ta bort-knapp -->
          <button
            v-if="idx > 0"
            type="button"
            class="delete-btn"
            :aria-label="`Ta bort ${row.anslutning}`"
            @click="removeItem(idx)"
          >
            <font-awesome-icon icon="trash" class="icon-trash" />
          </button>
          <span v-else class="cell-empty"></span>
        </div>
      </div>

      <button type="button" class="add-btn" :disabled="atInletLimit" @click="addItem">
        Lägg till inlopp
      </button>

      <p v-if="atInletLimit" class="field-hint field-hint--flush">
        Max {{ maxInlets }} inlopp per brunn.
      </p>

      <p class="connection-note">
        Som standard borras anslutningar med riktning mot brunnscentrum.<br />
        Vid ingjutning av delar för plaströr garanteras ej täthet mellan betong och plast.
      </p>
    </section>
  </form>
</template>

<script>
import { computeInletArrows } from '@/dialArrows.js'
import { store, newForm, MAX_INLETS, RORTYP_OPTIONS } from '@/store.js'

let uidCounter = 0

export default {
  name: 'OrderForm',
  props: {
    index: { type: Number, required: true },
  },
  data() {
    return {
      store,
      // Unikt suffix så att label/input-par inte krockar mellan brunnar.
      uid: ++uidCounter,
      maxInlets: MAX_INLETS,
      rortypOptions: RORTYP_OPTIONS,
    }
  },
  computed: {
    // Fallback skyddar mot en render mellan att en brunn tas bort och
    // att v-for hinner avmontera komponenten.
    form() {
      return this.store.forms[this.index] || newForm()
    },
    // En pil per inlopp med ifylld vinkel (geometri i @/dialArrows.js).
    inletArrows() {
      return computeInletArrows(this.form.items)
    },
    atInletLimit() {
      return (this.form.items || []).length - 1 >= MAX_INLETS
    },
  },
  created() {
    // Initiera alltid minst ett "Utlopp" med vinkel = 0
    if (!this.form.items || this.form.items.length === 0) {
      this.form.items = [
        { anslutning: 'Utlopp', dimension: '', rortyp: '', vinkel: '0', stalp: '', sandfang: '' },
      ]
    } else {
      this.form.items[0].vinkel = '0'
    }
  },
  methods: {
    addItem() {
      if (this.atInletLimit) return
      const next = this.form.items.length
      this.form.items.push({
        anslutning: `Inlopp nr. ${next}`,
        dimension: '',
        rortyp: '',
        vinkel: '',
        stalp: '',
        sandfang: '',
      })
    },
    removeItem(i) {
      if (i > 0) {
        this.form.items.splice(i, 1)
      }
    },
    validateAngles() {
      for (const ansl of this.form.items) {
        // Rör inte tomma fält – klampa bara ifyllda vinklar till 0–360.
        if (ansl.vinkel === '' || ansl.vinkel == null) continue
        ansl.vinkel = Math.min(Math.max(+ansl.vinkel, 0), 360)
      }
    },
  },
}
</script>

<style scoped>
.icon-trash {
  color: var(--color-benders-red);
  font-size: 1rem;
}

/* Sektionerna ligger redan inuti .well-block – ingen extra ram */
.well-section {
  margin-bottom: var(--space-xl);
}
.well-section:last-child {
  margin-bottom: 0;
}

/* Pil-overlay på urtavlan – en pil per inlopp */
.dial-image-wrapper {
  position: relative;
}
.dial-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  pointer-events: none;
  overflow: visible; /* låt toppbrickan synas utanför bildkanten */
}
.inlet-line {
  stroke: var(--color-benders-blue);
  stroke-width: 2;
}
.inlet-arrowhead {
  fill: var(--color-benders-blue);
}
.inlet-badge-bg {
  fill: var(--color-benders-blue);
}
.inlet-badge-text {
  fill: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: var(--font-base);
}
</style>
